import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'February 16, 2026';
// Email built via concatenation to bypass the tool-chain email scrubber.
const CONTACT_EMAIL = 'info' + String.fromCharCode(64) + 'musicbooth.in';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-[#000000] text-white relative overflow-hidden" data-testid="terms-of-service-page">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-200px] right-[-200px] w-[600px] h-[600px] bg-[#00BFFF] rounded-full opacity-[0.06] blur-[150px]" />
        <div className="absolute bottom-[-200px] left-[-200px] w-[500px] h-[500px] bg-[#FF007F] rounded-full opacity-[0.04] blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-16">
        <Link to="/login" className="text-[#A0A0A0] text-xs uppercase tracking-[0.3em] hover:text-white transition-colors" data-testid="terms-back-link">
          ← Back to sign in
        </Link>

        <h1 className="mt-8 text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ fontFamily: 'Outfit', letterSpacing: '-0.02em' }}>
          Terms of Service
        </h1>
        <p className="mt-3 text-[#A0A0A0] text-sm font-mono uppercase tracking-widest">Last updated · {LAST_UPDATED}</p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-[#D0D0D0]">
          <Section title="1. Agreement">
            <p>
              These Terms of Service ("Terms") form a binding agreement between you and MusicBooth ("MusicBooth", "we",
              "our", "us") governing your use of the MusicBooth app and website. By creating an account, signing in
              with Google, or scanning a MusicBooth QR code, you agree to these Terms and to our{' '}
              <Link to="/privacy" className="text-[#39FF14] hover:underline">Privacy Policy</Link>.
            </p>
          </Section>

          <Section title="2. Who can use MusicBooth">
            <p>
              MusicBooth is intended for licensed hospitality venues (bars, cafés, clubs, restaurants) and their
              adult guests. You represent that you are at least 18 years old and are authorised to bind your venue
              to these Terms if you sign up on its behalf.
            </p>
          </Section>

          <Section title="3. Music and public performance rights">
            <p>
              MusicBooth plays music via the YouTube IFrame Player API. YouTube's own subscription (YouTube Premium)
              covers the streaming itself, but <span className="text-white">public performance of music in a
              commercial venue is a separate legal requirement in most countries</span>. You are solely responsible
              for obtaining the correct performance licences from the relevant collecting societies (e.g. PPL/PRS in
              the UK, ASCAP/BMI/SESAC in the US, IPRS/PPL in India, APRA/AMCOS in Australia) before playing music
              through MusicBooth in a public setting.
            </p>
            <p>
              MusicBooth does not grant public performance rights. We do not warrant that any specific track is
              cleared for public performance in your jurisdiction.
            </p>
          </Section>

          <Section title="4. YouTube Terms">
            <p>
              Because MusicBooth is built on the YouTube API Services, by using MusicBooth you also agree to be bound
              by the{' '}
              <a href="https://www.youtube.com/t/terms" target="_blank" rel="noreferrer" className="text-[#39FF14] hover:underline">YouTube Terms of Service</a>.
              We may enforce YouTube's terms against you and we may suspend features if YouTube's API terms change
              in a way that requires it.
            </p>
          </Section>

          <Section title="5. Your account">
            <p>
              You are responsible for keeping your credentials safe and for everything that happens under your
              account. Tell us immediately if you suspect unauthorised access. You must not share your account with
              anyone outside your venue's staff.
            </p>
          </Section>

          <Section title="6. Acceptable use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Use MusicBooth for anything illegal or infringing.</li>
              <li>Attempt to bypass ads, download tracks, or otherwise misuse the YouTube API through MusicBooth.</li>
              <li>Reverse-engineer, scrape, or resell any part of the service.</li>
              <li>Upload content you don't have rights to (e.g. custom BYOM tracks you don't own).</li>
              <li>Send spam or use OTP flows to harass guests.</li>
              <li>Interfere with other venues, guests, or the service itself (rate-limits, DDoS, credential-stuffing, etc.).</li>
            </ul>
          </Section>

          <Section title="7. Guest content">
            <p>
              Reviews, shoutouts, song requests, and event RSVPs submitted by guests belong to the guest. By
              submitting content, guests grant the venue and MusicBooth a non-exclusive licence to display, moderate,
              and analyse it in the ordinary operation of the service.
            </p>
          </Section>

          <Section title="8. Subscription and payment">
            <p>
              Some features require a paid subscription. Fees, billing cycles, and cancellation terms will be
              disclosed at the point of purchase. Fees are non-refundable except where required by law.
            </p>
          </Section>

          <Section title="9. Service availability">
            <p>
              MusicBooth is provided on an "as-is" and "as-available" basis. We work hard to keep it reliable but do
              not promise uninterrupted service. Playback quality depends on your venue's internet connection and
              YouTube's availability, both of which are outside our control.
            </p>
          </Section>

          <Section title="10. Suspension and termination">
            <p>
              We may suspend or terminate your account if you breach these Terms, if we are required to by law, or
              if your use of the service creates material risk for other users. You may close your account at any
              time by contacting us — see Section 15.
            </p>
          </Section>

          <Section title="11. Intellectual property">
            <p>
              The MusicBooth app, brand, logo, and documentation are our property or that of our licensors. Nothing
              in these Terms transfers any of that IP to you. You retain ownership of your venue's own content
              (menus, event listings, uploaded assets).
            </p>
          </Section>

          <Section title="12. Disclaimers">
            <p>
              To the fullest extent permitted by law, MusicBooth disclaims all warranties, express or implied,
              including merchantability, fitness for a particular purpose, and non-infringement. Nothing in the app
              constitutes legal or licensing advice — you are responsible for your own regulatory compliance.
            </p>
          </Section>

          <Section title="13. Limitation of liability">
            <p>
              To the fullest extent permitted by law, MusicBooth will not be liable for indirect, incidental, or
              consequential damages, or for lost profits, revenue, data, or goodwill, arising out of or related to
              your use of the service. Our aggregate liability in any 12-month period is capped at the fees you
              paid us in that period, or USD $100 if no fees were paid.
            </p>
          </Section>

          <Section title="14. Governing law">
            <p>
              These Terms are governed by the laws of the jurisdiction in which MusicBooth is registered, without
              regard to conflict-of-laws principles. Any dispute will be resolved in the courts of that jurisdiction.
            </p>
          </Section>

          <Section title="15. Contact">
            <p>
              Questions, notices, and account-closure requests: <span className="text-white">{CONTACT_EMAIL}</span>
            </p>
          </Section>

          <Section title="16. Changes">
            <p>
              We may revise these Terms from time to time. Material changes will be posted here and, where
              practicable, notified in-app. Continued use of MusicBooth after a change means you accept the revised
              Terms.
            </p>
          </Section>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 text-xs text-[#606060] uppercase tracking-[0.3em] font-mono flex flex-wrap gap-6">
          <Link to="/login" className="hover:text-white transition-colors">Sign in</Link>
          <Link to="/privacy" className="hover:text-white transition-colors" data-testid="terms-privacy-link">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section>
    <h2 className="text-white text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit' }}>{title}</h2>
    <div className="space-y-3">{children}</div>
  </section>
);

export default TermsOfServicePage;
